/**
 * HelpContent — renders the active article's HTML and its related-topic links.
 * Mirrors Bug Tracker's HelpContent.vue. Internal topic links (rendered with
 * `data-help-topic`) are intercepted and routed through the owning help system
 * rather than the browser.
 */
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';

import { HelpSystemBase } from '../../help-system-base';

@Component({
  selector: 'app-help-content',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (system.isLoading$ | async) {
      <div class="help-content__loading">Loading…</div>
    } @else {
      <article class="help-content__body" [innerHTML]="system.html$ | async" (click)="onBodyClick($event)"></article>

      @if ((system.relatedTopics$ | async)?.length) {
        <nav class="help-content__related">
          <h4>Related</h4>
          <ul>
            @for (rel of system.relatedTopics$ | async; track rel.topic) {
              <li><a href="#" (click)="goTo($event, rel.topic)">{{ rel.title }}</a></li>
            }
          </ul>
        </nav>
      }
    }
  `,
  styles: [`
    .help-content__loading { padding: 1.5rem 0; color: #6b7280; }
    .help-content__body { color: #1f2937; font-size: 0.92rem; line-height: 1.6; }
    .help-content__body :is(h1,h2,h3,h4) { color: #111827; line-height: 1.3; margin: 1.2em 0 0.5em; }
    .help-content__body h1 { font-size: 1.35rem; }
    .help-content__body h2 { font-size: 1.15rem; }
    .help-content__body h3 { font-size: 1.02rem; }
    .help-content__body h4 { font-size: 0.95rem; }
    .help-content__body p { margin: 0.6em 0; }
    .help-content__body a { color: #2563eb; text-decoration: none; }
    .help-content__body a:hover { text-decoration: underline; }
    .help-content__body code { background: #f3f4f6; padding: 0.1em 0.35em; border-radius: 4px; font-size: 0.85em; }
    .help-content__body pre { background: #1f2937; color: #f9fafb; padding: 0.8em; border-radius: 8px; overflow-x: auto; }
    .help-content__body pre code { background: transparent; color: inherit; padding: 0; }
    .help-content__body ul, .help-content__body ol { padding-left: 1.3em; margin: 0.6em 0; }
    .help-content__body li { margin: 0.25em 0; }
    .help-content__body blockquote { border-left: 3px solid #d1d5db; margin: 0.8em 0; padding: 0.2em 0 0.2em 1em; color: #4b5563; }
    .help-content__body hr { border: 0; border-top: 1px solid #e5e7eb; margin: 1.2em 0; }
    .help-table { border-collapse: collapse; width: 100%; margin: 0.8em 0; font-size: 0.85rem; }
    .help-table th, .help-table td { border: 1px solid #e5e7eb; padding: 0.45em 0.6em; text-align: left; vertical-align: top; }
    .help-table th { background: #f9fafb; font-weight: 600; }
    .help-callout { border-radius: 8px; padding: 0.7em 0.9em; margin: 0.9em 0; font-size: 0.88rem; border: 1px solid; }
    .help-callout__title { font-weight: 600; margin-bottom: 0.25em; }
    .help-callout p { margin: 0.3em 0; }
    .help-callout--warning { background: #fffbeb; border-color: #fcd34d; color: #92400e; }
    .help-callout--info { background: #eff6ff; border-color: #93c5fd; color: #1e40af; }
    .help-callout--note { background: #f5f3ff; border-color: #c4b5fd; color: #5b21b6; }
    .help-callout--tip { background: #ecfdf5; border-color: #6ee7b7; color: #065f46; }
    .help-content__related { margin-top: 1.6em; border-top: 1px solid #e5e7eb; padding-top: 0.9em; }
    .help-content__related h4 { margin: 0 0 0.4em; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; }
    .help-content__related ul { list-style: none; padding: 0; margin: 0; }
    .help-content__related li { margin: 0.3em 0; }
    .help-content__related a { color: #2563eb; text-decoration: none; font-size: 0.9rem; }
    .help-content__related a:hover { text-decoration: underline; }
  `],
})
export class HelpContentComponent {
  @Input({ required: true }) system!: HelpSystemBase;

  onBodyClick(event: MouseEvent): void {
    const anchor = (event.target as HTMLElement).closest('a[data-help-topic]');
    if (!anchor) return;
    event.preventDefault();
    const topic = anchor.getAttribute('data-help-topic');
    if (topic) this.system.navigateTo(topic);
  }

  goTo(event: MouseEvent, topic: string): void {
    event.preventDefault();
    this.system.navigateTo(topic);
  }
}
